/**
 * Export utilities — CSV ve Excel (xlsx)
 *
 * Excel kütüphanesi (xlsx-js-style, ~900KB) sayfa bundle'ına girmesin diye
 * dinamik import edilir — sadece kullanıcı export'a bastığında yüklenir.
 * xlsx-js-style, SheetJS (xlsx) 0.18.5 fork'u: read/utils/writeFile API'si birebir
 * aynı + hücre stil desteği. Projede tek Excel kütüphanesi budur.
 */
let _xlsxPromise = null
export function loadXLSX() {
  if (!_xlsxPromise) {
    _xlsxPromise = import('xlsx-js-style').then((m) => m.default ?? m)
  }
  return _xlsxPromise
}

/**
 * Stok satırlarını export için düz objeye dönüştür
 */
function mapStockRows(items) {
  return items.map(item => ({
    'Malzeme Kodu':       item.material        || '',
    'Malzeme Adı':        item.material_text   || '',
    'Marka':              item.brand           || '',
    'Depo':               item.warehouse       || '',
    'Toplam Stok':        item.totalstock      ?? 0,
    'Kullanılabilir':     item.availstock      ?? 0,
    'Rezerve':            item.reservestock    ?? 0,
    'Liste Fiyatı (EUR)': item.list_price      ?? 0,
    'İskonto (%)':        item.disc_pct        ?? 0,
    'Stok Tutarı (EUR)':  item.stok_eur        ?? 0,
  }))
}

/**
 * CSV indirme
 */
export function exportCSV(items, filename = 'stok') {
  const rows = mapStockRows(items)
  if (!rows.length) return

  const headers = Object.keys(rows[0])
  const csvLines = [
    headers.join(';'),
    ...rows.map(r =>
      headers.map(h => {
        const v = r[h]
        // Sayıları Türkçe ondalık ayracıyla yaz
        if (typeof v === 'number') return String(v).replace('.', ',')
        // Metin içinde noktalı virgül varsa tırnak içine al
        if (typeof v === 'string' && v.includes(';')) return `"${v}"`
        return v
      }).join(';')
    ),
  ]

  const bom = '\uFEFF' // UTF-8 BOM — Excel Türkçe karakterleri doğru okusun
  const blob = new Blob([bom + csvLines.join('\r\n')], { type: 'text/csv;charset=utf-8;' })
  downloadBlob(blob, `${filename}_${today()}.csv`)
}

/**
 * Excel (.xlsx) indirme
 */
export async function exportExcel(items, filename = 'stok', companyLabel = '') {
  const rows = mapStockRows(items)
  if (!rows.length) return

  const XLSX = await loadXLSX()
  const ws = XLSX.utils.json_to_sheet(rows)

  // Sütun genişlikleri
  ws['!cols'] = [
    { wch: 18 }, // Malzeme Kodu
    { wch: 40 }, // Malzeme Adı
    { wch: 18 }, // Marka
    { wch: 12 }, // Depo
    { wch: 14 }, // Toplam
    { wch: 16 }, // Kullanılabilir
    { wch: 12 }, // Rezerve
    { wch: 18 }, // Liste Fiyatı
    { wch: 14 }, // İskonto
    { wch: 18 }, // Stok Tutarı
  ]

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Stok')

  // Özet sayfası
  const summary = [
    ['Rapor',       'Stok Durumu'],
    ['Firma',       companyLabel],
    ['Tarih',       new Date().toLocaleDateString('tr-TR')],
    ['Kayıt Sayısı', rows.length],
    ['Toplam Stok Tutarı (EUR)',
      rows.reduce((s, r) => s + (r['Stok Tutarı (EUR)'] || 0), 0).toFixed(2)],
  ]
  const wsSummary = XLSX.utils.aoa_to_sheet(summary)
  wsSummary['!cols'] = [{ wch: 26 }, { wch: 22 }]
  XLSX.utils.book_append_sheet(wb, wsSummary, 'Özet')

  XLSX.writeFile(wb, `${filename}_${today()}.xlsx`)
}

// ── Ortak Excel biçimlendirme ────────────────────────────────────────────────
// xlsx-js-style hücre stilini destekliyor (cell.s) ama uzun süre kullanılmadı;
// export'lar biçimsiz çıkıyordu. Aşağıdakiler tek yerde toplandı, böylece
// exportGenericExcel'i çağıran her sayfa aynı görünümü alır.

const INK = '1F2937'      // koyu gri — gövde metni
const MUTED = '6B7280'
const LINE = 'E5E7EB'
const HEAD_BG = '4F46E5'  // indigo — başlık bandı
const ZEBRA = 'F8FAFC'

const HEADER_STYLE = {
  font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } },
  fill: { patternType: 'solid', fgColor: { rgb: HEAD_BG } },
  alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
  border: {
    bottom: { style: 'thin', color: { rgb: HEAD_BG } },
    right: { style: 'thin', color: { rgb: 'FFFFFF' } },
  },
}

/** Başlık metninden sayı biçimi tahmin et (para birimi / yüzde / adet). */
function guessNumFmt(header) {
  const h = String(header || '')
  if (/%/.test(h)) return '0.00"%"'
  if (/€|\bEUR\b/i.test(h)) return '#,##0.00" €"'
  if (/₺|\bTL\b/i.test(h)) return '#,##0.00" ₺"'
  if (/\$|\bUSD\b/i.test(h)) return '#,##0.00" $"'
  if (/miktar|adet|sayı|gün/i.test(h)) return '#,##0'
  return '#,##0.00'
}

/** Kolon genişliği: başlık ve ilk 200 satırın içerik uzunluğuna göre. */
function autoWidths(rows, headers) {
  return headers.map((h) => {
    let max = String(h).length
    for (let i = 0; i < Math.min(rows.length, 200); i++) {
      const v = rows[i][h]
      if (v === null || v === undefined) continue
      const len = typeof v === 'number'
        ? String(Math.round(v)).length + 6      // ayraç + ondalık payı
        : String(v).length
      if (len > max) max = len
    }
    return { wch: Math.min(Math.max(max + 3, 10), 48) }
  })
}

/**
 * Bir sayfayı biçimlendir: başlık bandı, zebra satır, sayı biçimi,
 * otomatik filtre ve dondurulmuş başlık.
 */
function styleSheet(XLSX, ws, rows, opts = {}) {
  const headers = Object.keys(rows[0])
  const range = XLSX.utils.decode_range(ws['!ref'])
  const numFmt = opts.numFmt || {}
  const colorRules = opts.colorRules || {}   // { 'Kolon': (v, row) => 'RRGGBB' | null }
  const rowHighlight = opts.rowHighlight     // (row) => 'RRGGBB' | null

  for (let c = range.s.c; c <= range.e.c; c++) {
    const header = headers[c]
    const hCell = ws[XLSX.utils.encode_cell({ r: 0, c })]
    if (hCell) hCell.s = HEADER_STYLE

    for (let r = 1; r <= range.e.r; r++) {
      const cell = ws[XLSX.utils.encode_cell({ r, c })]
      if (!cell) continue
      const row = rows[r - 1]
      const isNum = typeof cell.v === 'number'

      if (isNum) cell.z = numFmt[header] || guessNumFmt(header)

      const hi = rowHighlight ? rowHighlight(row) : null
      // Öncelik: satır vurgusu > kolon zemini > zebra. Satır kırmızıysa kolon
      // rengi basılmaz, yoksa iki zemin çakışıp ikisi de okunmaz oluyor.
      const bg = hi || (opts.colBg && opts.colBg[header]) || (r % 2 === 0 ? ZEBRA : null)
      const fg = colorRules[header] ? colorRules[header](cell.v, row) : null

      cell.s = {
        font: { sz: 10, color: { rgb: fg || INK }, bold: !!fg },
        alignment: { horizontal: isNum ? 'right' : 'left', vertical: 'center' },
        border: { bottom: { style: 'hair', color: { rgb: LINE } } },
        ...(bg ? { fill: { patternType: 'solid', fgColor: { rgb: bg } } } : {}),
      }
    }
  }

  ws['!cols'] = opts.colWidths || autoWidths(rows, headers)
  ws['!rows'] = [{ hpt: 26 }]                                   // başlık yüksekliği
  ws['!autofilter'] = { ref: XLSX.utils.encode_range(range) }
  // Not: dondurulmuş başlık (freeze pane) EKLENMEDİ — SheetJS'in community
  // yazıcısı `!freeze`/`!views` alanlarını çıktıya işlemiyor (üretilen
  // sheet1.xml'de <sheetView> boş kalıyor, doğrulandı). Kullanıcı gerekirse
  // Excel'de Görünüm > Bölmeleri Dondur ile kendi ayarlar.
  return ws
}

/** Özet sayfası — başlık satırı kalın, etiket sütunu gri. */
function buildSummarySheet(XLSX, aoa) {
  const ws = XLSX.utils.aoa_to_sheet(aoa)
  const range = XLSX.utils.decode_range(ws['!ref'])
  for (let r = range.s.r; r <= range.e.r; r++) {
    const k = ws[XLSX.utils.encode_cell({ r, c: 0 })]
    const v = ws[XLSX.utils.encode_cell({ r, c: 1 })]
    const isTitle = r === 0
    if (k) {
      k.s = isTitle
        ? { font: { bold: true, sz: 13, color: { rgb: HEAD_BG } } }
        : { font: { sz: 10, color: { rgb: MUTED } } }
    }
    if (v) {
      v.s = {
        font: { bold: true, sz: isTitle ? 13 : 10, color: { rgb: INK } },
        alignment: { horizontal: typeof v.v === 'number' ? 'right' : 'left' },
      }
      if (typeof v.v === 'number') v.z = '#,##0.00'
    }
  }
  ws['!cols'] = [{ wch: 30 }, { wch: 26 }]
  return ws
}

// ── Generic Export (herhangi bir tablo için) ─────────────────────────────────

/**
 * Generic Excel export — herhangi bir veri seti için.
 * @param {Array} rows - Export edilecek satırlar (düz obje dizisi, key=kolon başlığı)
 * @param {string} sheetName - Sayfa adı (ör. "Stok Optimizasyonu")
 * @param {string} filename - Dosya adı prefix (ör. "stok-optimizasyon")
 * @param {string} companyLabel - Firma adı (özet sayfası için)
 * @param {Array} colWidths - Opsiyonel sütun genişlikleri [{wch: 18}, ...]
 * @param {Object} opts - Opsiyonel: { numFmt, colorRules, rowHighlight, summaryExtra, summaryTitle }
 */
export async function exportGenericExcel(rows, sheetName = 'Veri', filename = 'export', companyLabel = '', colWidths = null, opts = {}) {
  if (!rows.length) return

  const XLSX = await loadXLSX()
  const ws = XLSX.utils.json_to_sheet(rows)
  styleSheet(XLSX, ws, rows, { ...opts, colWidths })

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31))   // Excel sınırı

  const summary = [
    [opts.summaryTitle || sheetName, ''],
    ['Firma', companyLabel],
    ['Tarih', new Date().toLocaleDateString('tr-TR')],
    ['Kayıt Sayısı', rows.length],
    ...(opts.summaryExtra || []),
  ]
  XLSX.utils.book_append_sheet(wb, buildSummarySheet(XLSX, summary), 'Özet')

  XLSX.writeFile(wb, `${filename}_${today()}.xlsx`)
}

/**
 * Generic CSV export — herhangi bir veri seti için.
 * @param {Array} rows - Export edilecek satırlar (düz obje dizisi, key=kolon başlığı)
 * @param {string} filename - Dosya adı prefix
 */
export function exportGenericCSV(rows, filename = 'export') {
  if (!rows.length) return

  const headers = Object.keys(rows[0])
  const csvLines = [
    headers.join(';'),
    ...rows.map(r =>
      headers.map(h => {
        const v = r[h]
        if (v === null || v === undefined) return ''
        if (typeof v === 'number') return String(v).replace('.', ',')
        const s = String(v)
        if (s.includes(';') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`
        return s
      }).join(';')
    ),
  ]

  const bom = '\uFEFF'
  const blob = new Blob([bom + csvLines.join('\r\n')], { type: 'text/csv;charset=utf-8;' })
  downloadBlob(blob, `${filename}_${today()}.csv`)
}

// ── Yardımcı ──────────────────────────────────────────────────────────────────

function today() {
  return new Date().toISOString().slice(0, 10).replace(/-/g, '')
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob)
  const a   = document.createElement('a')
  a.href     = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * Çok sayfalı Excel — her sayfa {name, rows}; boş sayfa atlanır, en sona Özet.
 * Denetim paketi gibi birden çok dökümün tek dosyada verildiği yerler için.
 */
export async function exportMultiSheetExcel(sheets, filename = 'export', companyLabel = '', summaryExtra = []) {
  const list = (sheets || []).filter((s) => s.rows && s.rows.length)
  if (!list.length) return
  const XLSX = await loadXLSX()
  const wb = XLSX.utils.book_new()
  const used = new Set()
  for (const s of list) {
    const ws = XLSX.utils.json_to_sheet(s.rows)
    styleSheet(XLSX, ws, s.rows, s.opts || {})
    let name = String(s.name || 'Sayfa').replace(/[\\/?*[\]:]/g, ' ').slice(0, 31)
    while (used.has(name)) name = `${name.slice(0, 28)}_${used.size}`
    used.add(name)
    XLSX.utils.book_append_sheet(wb, ws, name)
  }
  const summary = [
    ['Denetim Paketi', ''],
    ['Firma', companyLabel],
    ['Hazırlanma', new Date().toLocaleString('tr-TR')],
    ...summaryExtra,
    ...list.map((s) => [s.name, `${s.rows.length} satır`]),
  ]
  XLSX.utils.book_append_sheet(wb, buildSummarySheet(XLSX, summary), 'Özet')
  XLSX.writeFile(wb, `${filename}_${today()}.xlsx`)
}
